import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { PlaylistSummary } from "../api/types";
import { navigate } from "../hooks/useLocation";
import { usePersistentTab } from "../hooks/usePersistentTab";

export interface PlaylistTabsProps {
  playlists: PlaylistSummary[];
  pendingRemoveKey: string | null;
  onRemoveFromPlaylist: (playlistSlug: string, jobId: string) => Promise<void> | void;
}

export const ACTIVE_PLAYLIST_STORAGE_KEY = "kareoke.activePlaylistSlug";

function isNonEmptyString(value: string): value is string {
  return value.length > 0;
}

function removeKey(playlistSlug: string, jobId: string): string {
  return `${playlistSlug}:${jobId}`;
}

export function PlaylistTabs({
  playlists,
  pendingRemoveKey,
  onRemoveFromPlaylist,
}: PlaylistTabsProps) {
  const { t } = useTranslation();
  const [activeSlug, setActiveSlug] = usePersistentTab<string>(
    ACTIVE_PLAYLIST_STORAGE_KEY,
    "",
    isNonEmptyString,
  );
  const [confirmingJobId, setConfirmingJobId] = useState<string | null>(null);
  const [previousPlaylists, setPreviousPlaylists] = useState(playlists);

  // Render-time state adjustment (see https://react.dev/learn/you-might-not-need-an-effect)
  // — when the active slug is missing from the available playlists (either on
  // first render with a stale persisted value, or because the playlists prop
  // changed underneath us), snap to the first playlist and overwrite storage
  // so it stops pointing at a stale slug.
  const activeSlugMissing =
    playlists.length > 0 && !playlists.some((playlist) => playlist.slug === activeSlug);
  if (playlists !== previousPlaylists) {
    setPreviousPlaylists(playlists);
    if (activeSlugMissing) {
      setActiveSlug(playlists[0]?.slug ?? "");
      setConfirmingJobId(null);
    }
  } else if (activeSlugMissing) {
    setActiveSlug(playlists[0]?.slug ?? "");
    setConfirmingJobId(null);
  }

  const activePlaylist =
    playlists.find((playlist) => playlist.slug === activeSlug) ?? playlists[0];

  if (playlists.length === 0 || activePlaylist === undefined) {
    return (
      <section className="space-y-4">
        <p className="text-sm text-stone-400">{t("playlists.emptyPlaylists")}</p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div
        role="tablist"
        aria-label={t("playlists.tablistLabel")}
        className="flex flex-wrap gap-2 border-b border-stone-800"
      >
        {playlists.map((playlist) => {
          const selected = playlist.slug === activePlaylist.slug;
          return (
            <button
              key={playlist.slug}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => {
                setActiveSlug(playlist.slug);
                setConfirmingJobId(null);
              }}
              className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 ${
                selected
                  ? "border-brand-500 text-brand-300"
                  : "border-transparent text-stone-400 hover:text-stone-100"
              }`}
            >
              {playlist.name}
            </button>
          );
        })}
      </div>

      {activePlaylist.songs.length === 0 ? (
        <p className="text-sm text-stone-400">{t("playlists.emptySongs")}</p>
      ) : (
        <ul className="divide-y divide-stone-800 border-y border-stone-800">
          {activePlaylist.songs.map((song) => {
            const isConfirming = confirmingJobId === song.job_id;
            const isRemoving =
              pendingRemoveKey === removeKey(activePlaylist.slug, song.job_id);
            return (
              <li
                key={song.job_id}
                className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/songs/${song.job_id}`)}
                  className="min-w-0 flex-1 truncate text-left text-sm font-medium text-stone-100 transition-colors hover:text-brand-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950"
                >
                  {song.title}
                </button>
                {isConfirming ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={isRemoving}
                      onClick={() => {
                        void Promise.resolve(
                          onRemoveFromPlaylist(activePlaylist.slug, song.job_id),
                        )
                          .then(() => setConfirmingJobId(null))
                          .catch(() => undefined);
                      }}
                      className="rounded-md border border-red-400 px-2.5 py-1 text-xs text-red-100 transition-colors hover:bg-red-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t("playlists.confirm")}
                    </button>
                    <button
                      type="button"
                      disabled={isRemoving}
                      onClick={() => setConfirmingJobId(null)}
                      className="rounded-md border border-stone-700 px-2.5 py-1 text-xs text-stone-200 transition-colors hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {t("playlists.cancel")}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingJobId(song.job_id)}
                    className="rounded-md border border-stone-700 px-2.5 py-1 text-xs text-stone-200 transition-colors hover:bg-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950"
                  >
                    {t("playlists.remove")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
